/**
 * Unified Authentication Context Provider
 * 
 * Provides a consistent authentication interface that works with both
 * AWS Cognito (production) and local mock authentication (development).
 * Uses factory pattern to select the appropriate auth service based on environment.
 * 
 * Requirements: 18.8, 18.11
 */

import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, AuthToken, UserRole } from '../types/auth';
import { authService } from '../services/authService';

// ============================================================================
// Types
// ============================================================================

interface AuthContextType {
  user: User | null;
  token: AuthToken | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ requiresPasswordChange?: boolean }>;
  logout: () => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  completeNewPassword: (newPassword: string) => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<void>;
  updateProfile: (name: string, photoFile?: File) => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  getAuthToken: () => string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================================================
// Hook
// ============================================================================

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ============================================================================
// Provider Props
// ============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

// ============================================================================
// Provider Implementation
// ============================================================================

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<AuthToken | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Check for existing authentication session
   */
  const checkAuth = useCallback(async () => {
    try {
      // Check Cognito session
      const session = await authService.getCurrentSession();
      if (session) {
        setUser(session.user);
        setToken(session.token);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  /**
   * Standard login with email and password (Cognito)
   */
  const login = async (email: string, password: string) => {
    const result = await authService.login(email, password);
    
    if (result.requiresPasswordChange) {
      return { requiresPasswordChange: true };
    }

    setUser(result.user);
    setToken(result.token);
    return {};
  };

  /**
   * Logout from current session
   */
  const logout = async () => {
    await authService.logout();
    setUser(null);
    setToken(null);
  };

  /**
   * Change password (Cognito only)
   */
  const changePassword = async (oldPassword: string, newPassword: string) => {
    await authService.changePassword(oldPassword, newPassword);
  };

  /**
   * Complete new password challenge (for first-time login)
   */
  const completeNewPassword = async (newPassword: string) => {
    const result = await authService.completeNewPassword(newPassword);
    setUser(result.user);
    setToken(result.token);
  };

  /**
   * Request password reset (Cognito only)
   */
  const requestPasswordReset = async (email: string) => {
    await authService.requestPasswordReset(email);
  };

  /**
   * Confirm password reset with code (Cognito only)
   */
  const confirmPasswordReset = async (email: string, code: string, newPassword: string) => {
    await authService.confirmPasswordReset(email, code, newPassword);
  };

  /**
   * Update user profile (name and photo)
   * Merges the updated user with existing user to preserve fields not returned by the API (like role)
   */
  const updateProfile = async (name: string, photoFile?: File) => {
    const updatedUser = await authService.updateProfile(name, photoFile);
    // Merge with existing user, but only use valid values from the update
    // This preserves role and other fields that the profile update API doesn't return correctly
    setUser(prevUser => {
      if (!prevUser) return updatedUser;
      
      // Create merged user starting with previous values
      const merged: User = { ...prevUser };
      
      // Only update specific fields that the profile update endpoint can change
      // This is more explicit than filtering undefined - we know exactly what can change
      if (updatedUser.name) {
        merged.name = updatedUser.name;
      }
      if (updatedUser.profilePhotoKey !== undefined) {
        merged.profilePhotoKey = updatedUser.profilePhotoKey;
      }
      if (updatedUser.phoneNumber) {
        merged.phoneNumber = updatedUser.phoneNumber;
      }
      
      return merged;
    });
  };

  /**
   * Check if user has a specific role
   */
  const hasRole = (role: UserRole): boolean => {
    return user?.role === role;
  };

  /**
   * Get the current auth token
   */
  const getAuthToken = (): string | null => {
    return token?.idToken || null;
  };

  // ============================================================================
  // Context Value
  // ============================================================================

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    changePassword,
    completeNewPassword,
    requestPasswordReset,
    confirmPasswordReset,
    updateProfile,
    hasRole,
    getAuthToken
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
